import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { useRubiaGallegaExperience } from '@/hooks/useRubiaGallegaExperience';
import { Phone, Wine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EXPERIENCE_CONFIG = {
  legacy: {
    heroImage: '/img/location/IMG_8110.jpg',
    bookingFormUrl:
      'https://rgradcpiwm98zpetuigzzghomf3fmzoq.forms.pienissimo.pro/?id=x6SKZY',
    eventName: 'Wagyu Experience - Brace e Sapori',
    startDate: '2026-06-26',
    offerPrice: '90',
  },
  rubia: {
    heroImage: '/img/IMG_0372.jpeg',
    bookingFormUrl:
      'https://rgradcpiwm98zpetuigzzghomf3fmzoq.forms.pienissimo.pro/?id=Ciu4Ya',
    eventName: 'La Rubia Gallega con Discarlux - Brace e Sapori',
    startDate: '2026-07-23',
    offerPrice: '85',
  },
} as const;

type Course = {
  number: string;
  title: string;
  category: string;
  description: string;
};

type PriceOption = {
  title: string;
  amount: string;
};

const WagyuExperience = () => {
  const isRubiaLive = useRubiaGallegaExperience();
  const variant = isRubiaLive ? 'rubia' : 'legacy';
  const config = EXPERIENCE_CONFIG[variant];
  const { t } = useTranslation(['wagyuExperience', 'seo']);

  const prefix = `wagyuExperience:${variant}`;
  const tk = (key: string) => t(`${prefix}.${key}`);
  const tka = <T,>(key: string) => t(`${prefix}.${key}`, { returnObjects: true }) as T;

  const courses = tka<Course[]>('menu.courses');
  const contactPhone = tk('contact.phone');

  const reservationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FoodEvent',
    name: config.eventName,
    description: t(`seo:wagyuExperience.${variant}.description`),
    startDate: config.startDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Restaurant',
      name: 'Brace e Sapori',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via P. B. Moscatelli, 8',
        addressLocality: 'San Giorgio Ionico',
        addressRegion: 'TA',
        addressCountry: 'IT',
      },
    },
    organizer: {
      '@type': 'Organization',
      name: 'Brace e Sapori',
      url: 'https://braceesapori.it',
    },
    ...(config.offerPrice
      ? {
          offers: {
            '@type': 'Offer',
            price: config.offerPrice,
            priceCurrency: 'EUR',
          },
        }
      : {}),
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t(`seo:wagyuExperience.${variant}.title`)}
        description={t(`seo:wagyuExperience.${variant}.description`)}
        keywords={t(`seo:wagyuExperience.${variant}.keywords`)}
        ogImage={config.heroImage}
        logicalPath="/experience"
        structuredData={reservationStructuredData}
      />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={config.heroImage}
            alt={tk('hero.imageAlt')}
            className="h-full w-full object-cover object-center"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>

        <div className="relative z-10 flex min-h-[70vh] flex-col items-center justify-center px-6 py-32 text-center">
          <ScrollReveal>
            <h1 className="mb-2 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl lg:text-8xl">
              {tk('hero.title')}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p className="mb-8 font-serif text-5xl italic text-foreground md:text-6xl lg:text-7xl">
              {tk('hero.subtitle')}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="mb-10 max-w-xl text-base font-light italic leading-relaxed text-muted-foreground md:text-lg">
              {tk('hero.tagline')}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={300}>
            <div className="rounded-sm border border-primary/30 bg-background/60 px-8 py-4 backdrop-blur-sm">
              <p className="text-sm font-light uppercase tracking-[0.35em] text-primary md:text-base">
                {tk('hero.date')}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Menu */}
      <section className="py-24 px-6">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="mb-16 text-center">
              <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
                {tk('menu.label')}
              </span>
            </div>
          </ScrollReveal>

          <div className="space-y-16">
            {courses.map((course, index) => (
              <ScrollReveal key={course.number} delay={index * 80}>
                <article className="group">
                  <div className="flex gap-6 md:gap-10">
                    <span
                      className="shrink-0 text-6xl font-extralight leading-none text-foreground/20 transition-colors group-hover:text-primary/40 md:text-8xl"
                      aria-hidden="true"
                    >
                      {course.number}
                    </span>
                    <div className="pt-2 md:pt-4">
                      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h2 className="text-lg font-semibold uppercase tracking-wide text-foreground md:text-xl">
                          {course.title}
                        </h2>
                        <span className="text-xs font-light uppercase tracking-[0.2em] text-primary">
                          ({course.category})
                        </span>
                      </div>
                      <p className="text-sm font-light leading-relaxed text-muted-foreground md:text-base">
                        {course.description}
                      </p>
                    </div>
                  </div>
                  {index < courses.length - 1 && (
                    <div className="mt-16 h-px w-full bg-border" />
                  )}
                </article>
              </ScrollReveal>
            ))}
          </div>

          {variant === 'legacy' ? (
            <LegacyExtras tk={tk} tka={tka} />
          ) : (
            <RubiaPrice tk={tk} />
          )}

          <ScrollReveal delay={500}>
            <div className="mt-20 flex flex-col items-center gap-2 text-center">
              <p className="text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
                {tk('contact.phoneLabel')}
              </p>
              <a
                href={`tel:+39${contactPhone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-2 text-lg font-light text-foreground transition-colors hover:text-primary"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {contactPhone}
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {variant === 'legacy' ? (
        <LegacyGardenSection tk={tk} tka={tka} />
      ) : (
        <RubiaStorySection tk={tk} tka={tka} />
      )}

      {/* Booking */}
      <section className="pb-24 px-6 pt-10">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="mb-8 text-center">
              <h2 className="mb-4 text-3xl font-semibold text-foreground md:text-4xl">
                {tk('booking.title')}
              </h2>
              <p className="text-muted-foreground">{tk('booking.subtitle')}</p>
            </div>
          </ScrollReveal>

          <div className="relative w-full overflow-hidden rounded-lg border border-border bg-card">
            <iframe
              src={config.bookingFormUrl}
              className="h-[1500px] w-full border-0 md:h-[1500px]"
              title={tk('booking.iframeTitle')}
              allow="clipboard-write"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

type TranslateFn = (key: string) => string;
type TranslateArrayFn = <T>(key: string) => T;

const LegacyExtras = ({
  tk,
  tka,
}: {
  tk: TranslateFn;
  tka: TranslateArrayFn;
}) => {
  const priceOptions = tka<PriceOption[]>('price.options');

  return (
    <ScrollReveal delay={480}>
      <div className="mt-20 text-center">
        <span className="mb-8 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
          {tk('price.label')}
        </span>
        <div className="space-y-8">
          {priceOptions.map((option) => (
            <div key={option.title}>
              <p className="mb-2 text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
                {option.title}
              </p>
              <p className="text-4xl font-semibold text-foreground md:text-5xl">
                {option.amount}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
          {tk('price.perPerson')}
        </p>
      </div>
    </ScrollReveal>
  );
};

const RubiaPrice = ({ tk }: { tk: TranslateFn }) => (
  <ScrollReveal delay={480}>
    <div className="mt-20 text-center">
      <span className="mb-8 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
        {tk('price.label')}
      </span>
      <div className="space-y-8">
        <div>
          <p className="mb-2 text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
            {tk('price.title')}
          </p>
          <p className="text-4xl font-semibold text-foreground md:text-5xl">
            {tk('price.amount')}
          </p>
        </div>
      </div>
      <p className="mt-8 text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
        {tk('price.perPerson')}
      </p>
    </div>
  </ScrollReveal>
);

const LegacyGardenSection = ({
  tk,
  tka,
}: {
  tk: TranslateFn;
  tka: TranslateArrayFn;
}) => {
  const gardenParagraphs = tka<string[]>('garden.paragraphs');

  return (
    <section className="border-y border-border bg-card py-24 px-6">
      <div className="mx-auto max-w-3xl">
        <ScrollReveal>
          <div className="mb-12 text-center">
            <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
              {tk('garden.label')}
            </span>
            <h2 className="mb-6 text-3xl font-semibold text-foreground md:text-4xl">
              {tk('garden.title')}
            </h2>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="mb-12 space-y-6 text-center">
            {gardenParagraphs.map((paragraph, index) => (
              <p
                key={index}
                className="text-base font-light leading-relaxed text-muted-foreground md:text-lg"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="rounded-lg border border-border bg-background p-8 text-center md:p-12">
            <div className="mb-6 flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20">
                <Wine className="h-7 w-7 text-primary" aria-hidden="true" />
              </div>
            </div>
            <p className="mb-4 text-sm font-light uppercase tracking-[0.25em] text-primary">
              {tk('garden.schedule')}
            </p>
            <p className="mb-6 text-lg font-semibold uppercase tracking-wide text-foreground md:text-xl">
              {tk('garden.offer')}
            </p>
            <p className="font-serif text-xl italic text-muted-foreground md:text-2xl">
              {tk('garden.tagline')}
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

const RubiaStorySection = ({
  tk,
  tka,
}: {
  tk: TranslateFn;
  tka: TranslateArrayFn;
}) => {
  const storyParagraphs = tka<string[]>('story.paragraphs');

  return (
    <section className="border-y border-border bg-card py-24 px-6">
      <div className="mx-auto max-w-3xl">
        <ScrollReveal>
          <div className="mb-12 text-center">
            <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
              {tk('story.label')}
            </span>
            <h2 className="mb-6 text-3xl font-semibold text-foreground md:text-4xl">
              {tk('story.title')}
            </h2>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="mb-12 space-y-6 text-center">
            {storyParagraphs.map((paragraph, index) => (
              <p
                key={index}
                className="text-base font-light leading-relaxed text-muted-foreground md:text-lg"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={150}>
          <div className="mb-12 text-center">
            <p className="mb-3 text-sm font-light uppercase tracking-[0.25em] text-primary">
              {tk('wine.label')}
            </p>
            <p className="mb-2 text-sm font-light uppercase tracking-[0.2em] text-muted-foreground">
              {tk('wine.curatedBy')}
            </p>
            <p className="mb-2 text-2xl font-semibold text-foreground md:text-3xl">
              {tk('wine.specialistName')}
            </p>
            <p className="font-serif text-lg italic text-muted-foreground md:text-xl">
              {tk('wine.specialistRole')}
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="rounded-lg border border-border bg-background p-8 text-center md:p-12">
            <div className="mb-6 flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20">
                <Wine className="h-7 w-7 text-primary" aria-hidden="true" />
              </div>
            </div>
            <p className="mb-4 text-sm font-light uppercase tracking-[0.25em] text-primary">
              {tk('story.schedule')}
            </p>
            <p className="mb-6 text-lg font-semibold uppercase tracking-wide text-foreground md:text-xl">
              {tk('story.offer')}
            </p>
            <p className="font-serif text-xl italic text-muted-foreground md:text-2xl">
              {tk('story.tagline')}
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default WagyuExperience;
